import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown } from "lucide-react";
import { getAppPortalRoot } from "../utils/appPortal";
import { getAppScale } from "../utils/appScale";
import "./PackAssignDropdown.css";

const MENU_GAP = 6;
const VIEWPORT_PADDING = 8;

function applyMatchTriggerWidth(style, triggerEl, scale) {
  const triggerWidth = triggerEl?.getBoundingClientRect().width;
  if (!triggerWidth) return;
  const layoutWidth = triggerWidth / scale;
  style.width = layoutWidth;
  style.minWidth = layoutWidth;
  style.maxWidth = layoutWidth;
}

function computeMenuStyle(triggerEl, menuEl, { forceBelow = false, maxHeight = 240 } = {}) {
  if (!triggerEl) return null;

  const scale = getAppScale();
  const rect = triggerEl.getBoundingClientRect();
  const menuLayoutHeight = menuEl?.offsetHeight || 0;
  const menuVisualHeight = menuLayoutHeight * scale;
  const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_PADDING;
  const spaceAbove = rect.top - VIEWPORT_PADDING;
  const estimatedVisualHeight =
    menuVisualHeight || Math.min(maxHeight * scale, window.innerHeight - VIEWPORT_PADDING * 2);

  const openAbove = forceBelow
    ? false
    : menuVisualHeight > 0
      ? spaceBelow < menuVisualHeight && spaceAbove >= spaceBelow
      : spaceBelow < estimatedVisualHeight && spaceAbove > spaceBelow;

  const availableVisual = (openAbove ? spaceAbove : spaceBelow) - MENU_GAP;
  const visualMaxHeight = Math.min(
    maxHeight * scale,
    Math.max(80 * scale, availableVisual)
  );

  const style = {
    left: rect.left,
    minWidth: rect.width / scale,
    maxWidth: Math.min(420, window.innerWidth - VIEWPORT_PADDING * 2) / scale,
    maxHeight: visualMaxHeight / scale,
    transformOrigin: openAbove ? "bottom left" : "top left",
  };

  if (openAbove) {
    style.top = rect.top - MENU_GAP;
    style.transform =
      scale === 1 ? "translateY(-100%)" : `translateY(-100%) scale(${scale})`;
  } else {
    style.top = rect.bottom + MENU_GAP;
    style.transform = scale === 1 ? "none" : `scale(${scale})`;
  }

  return { style, openAbove, scale };
}

function sameMenuStyle(current, next) {
  if (current === next) return true;
  if (!current || !next) return false;
  return (
    current.left === next.left &&
    current.top === next.top &&
    current.minWidth === next.minWidth &&
    current.maxWidth === next.maxWidth &&
    current.maxHeight === next.maxHeight &&
    current.width === next.width &&
    current.transform === next.transform &&
    current.transformOrigin === next.transformOrigin
  );
}

export default function PackAssignDropdown({
  options = [],
  value,
  onChange,
  disabled = false,
  ariaLabel = "Choisir une affectation",
  fixedMenuWidth = null,
  matchTriggerWidth = false,
  menuMaxHeight = 240,
  menuZIndex = 100000,
  openBelow = false,
  menuClassName = "",
  placeholder = null,
  edgeFade = false,
  getMenuScrollTop = null,
  onMenuScroll = null,
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const [menuAbove, setMenuAbove] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const frameRef = useRef(null);

  const selectedOption = useMemo(() => {
    const found = options.find((option) => option.value === value);
    if (found) return found;
    if (placeholder != null) {
      return { value: "", label: placeholder };
    }
    return options[0];
  }, [options, value, placeholder]);

  const updateMenuPosition = useCallback(() => {
    const result = computeMenuStyle(triggerRef.current, menuRef.current, {
      forceBelow: openBelow,
      maxHeight: menuMaxHeight,
    });
    if (!result) return;

    if (matchTriggerWidth) {
      applyMatchTriggerWidth(result.style, triggerRef.current, result.scale);
    }

    setMenuStyle((current) =>
      sameMenuStyle(current, result.style) ? current : result.style
    );
    setMenuAbove((current) =>
      current === result.openAbove ? current : result.openAbove
    );
  }, [matchTriggerWidth, openBelow, menuMaxHeight]);

  useLayoutEffect(() => {
    if (!dropdownOpen) return undefined;
    updateMenuPosition();
    const raf = requestAnimationFrame(updateMenuPosition);
    return () => cancelAnimationFrame(raf);
  }, [dropdownOpen, options, updateMenuPosition]);

  useLayoutEffect(() => {
    if (!dropdownOpen) return undefined;
    const applyScroll = () => {
      const menu = menuRef.current;
      const saved = getMenuScrollTop?.();
      if (!menu || !Number.isFinite(saved) || saved <= 0) return;
      menu.scrollTop = saved;
    };
    applyScroll();
    const raf = requestAnimationFrame(applyScroll);
    return () => cancelAnimationFrame(raf);
  }, [dropdownOpen, getMenuScrollTop]);

  useEffect(() => {
    if (!dropdownOpen) return undefined;

    const handlePointerDown = (event) => {
      if (rootRef.current?.contains(event.target)) return;
      if (frameRef.current?.contains(event.target)) return;
      if (menuRef.current?.contains(event.target)) return;
      setDropdownOpen(false);
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") setDropdownOpen(false);
    };

    const handleReposition = () => updateMenuPosition();

    document.addEventListener("mousedown", handlePointerDown, true);
    document.addEventListener("keydown", handleEscape);
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);
    window.visualViewport?.addEventListener("resize", handleReposition);
    window.visualViewport?.addEventListener("scroll", handleReposition);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown, true);
      document.removeEventListener("keydown", handleEscape);
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
      window.visualViewport?.removeEventListener("resize", handleReposition);
      window.visualViewport?.removeEventListener("scroll", handleReposition);
    };
  }, [dropdownOpen, updateMenuPosition]);

  const handleSelect = (nextValue) => {
    onChange(nextValue);
    setDropdownOpen(false);
  };

  if (!selectedOption) return null;

  const positionedStyle = {
    ...(menuStyle || {}),
    ...(matchTriggerWidth
      ? {}
      : fixedMenuWidth
        ? {
            width: fixedMenuWidth,
            minWidth: fixedMenuWidth,
            maxWidth: fixedMenuWidth,
          }
        : {}),
    maxHeight: menuStyle?.maxHeight ?? menuMaxHeight,
    zIndex: menuZIndex,
  };

  const optionItems = options.map((option) => (
    <li key={option.value} role="presentation">
      <button
        type="button"
        role="option"
        aria-selected={option.value === value}
        className={`pack-assign-dropdown__option${
          option.value === value ? " pack-assign-dropdown__option--selected" : ""
        }`}
        onClick={() => handleSelect(option.value)}
      >
        {option.dotClass ? (
          <span className={`pack-assign-dropdown__dot ${option.dotClass}`} aria-hidden="true" />
        ) : null}
        <span className="pack-assign-dropdown__option-label">{option.label}</span>
        {option.value === value && (
          <Check size={14} strokeWidth={2.2} aria-hidden="true" />
        )}
      </button>
    </li>
  ));

  const menuList = (
    <ul
      ref={menuRef}
      className={`pack-assign-dropdown__menu pack-assign-dropdown__menu--portal${
        menuAbove ? " pack-assign-dropdown__menu--above" : ""
      }${menuClassName ? ` ${menuClassName}` : ""}`}
      style={
        edgeFade
          ? { maxHeight: positionedStyle.maxHeight, overflowY: "auto" }
          : { ...positionedStyle, overflowY: "auto" }
      }
      role="listbox"
      onWheel={(event) => event.stopPropagation()}
      onScroll={(event) => onMenuScroll?.(event.currentTarget.scrollTop)}
    >
      {optionItems}
    </ul>
  );

  const menu = !dropdownOpen ? null : edgeFade ? (
    <div
      ref={frameRef}
      className={`pack-assign-dropdown__frame${
        menuClassName ? ` ${menuClassName}-frame` : ""
      }`}
      style={{ ...positionedStyle, position: "fixed" }}
    >
      {menuList}
    </div>
  ) : (
    menuList
  );

  return (
    <div
      className={`pack-assign-dropdown${
        dropdownOpen ? " pack-assign-dropdown--open" : ""
      }${disabled ? " pack-assign-dropdown--disabled" : ""}`}
      ref={rootRef}
    >
      <button
        ref={triggerRef}
        type="button"
        className="pack-assign-dropdown__trigger"
        aria-haspopup="listbox"
        aria-expanded={dropdownOpen}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setDropdownOpen((open) => {
            const next = !open;
            if (next) {
              const result = computeMenuStyle(triggerRef.current, null, {
                forceBelow: openBelow,
                maxHeight: menuMaxHeight,
              });
              if (result) {
                if (matchTriggerWidth) {
                  applyMatchTriggerWidth(result.style, triggerRef.current, result.scale);
                }
                setMenuStyle(result.style);
                setMenuAbove(result.openAbove);
              }
            }
            return next;
          });
        }}
      >
        {selectedOption.dotClass ? (
          <span
            className={`pack-assign-dropdown__dot ${selectedOption.dotClass}`}
            aria-hidden="true"
          />
        ) : null}
        <span className="pack-assign-dropdown__label">{selectedOption.label}</span>
        <ChevronDown
          size={16}
          strokeWidth={2}
          className="pack-assign-dropdown__chevron"
          aria-hidden="true"
        />
      </button>

      {menu && createPortal(menu, getAppPortalRoot())}
    </div>
  );
}
